import type { SystemFactory } from '../core/system';
import { createCameraRigSystem } from '../camera/cameraRig';
import { createPlayerSystem } from '../player/playerSystem';
import { createPlayerViewSystem } from '../player/playerView';
import { createOcclusionSilhouetteSystem } from '../render/occlusionSilhouette';
import { createVehiclePostSystem, createVehicleSystem } from '../vehicles/vehicleSystem';
import { createParticleSystem } from '../vehicles/fx/particles';
import { createVehicleFxSystem } from '../vehicles/fx/vehicleFx';
import { createVehicleViewSystem } from '../vehicles/vehicleView';
import { createAmbientFxSystem } from '../fx/ambientFx';
import { createEnvFxSystem } from '../fx/envFx';
import { createExplosionFxSystem } from '../fx/explosionFx';
import { createFootstepFxSystem } from '../fx/footstepFx';
import { createImpactFxSystem } from '../fx/impactFx';
import { createJuiceSystem } from '../fx/juice';
import { createMeleeFxSystem } from '../fx/meleeFx';
import { createPropSystem } from '../props/props';
import { createRenderSystem, createSceneLightingSystem } from '../render/renderSystem';
import { createTimeOfDaySystem } from '../render/timeOfDay';
import { createSaveSystem } from '../save/saveSystem';
import { createEconomySystem } from '../economy/economySystem';
import { createMissionSystem } from '../missions/missionSystem';
import { createAudioSystem } from '../audio/audioSystem';
import { createUiSystem } from '../ui/uiSystem';
import { createTouchControlsSystem } from '../ui/touchControls';
import { createLoadingSteps } from '../ui/loading';
import { createBookSystem } from '../book/bookSystem';
import { createOnboardingSystem } from '../onboarding/onboardingSystem';
import { createRouteArrowsSystem } from '../onboarding/routeView';
import { createActivitySystemFactories } from '../activities/activitySystem';
import { createContactsSystem } from '../contacts/contactsSystem';
import { createMayhemSystem } from '../activities/mayhemSystem';
import { createCarJobSystem } from '../activities/carJobSystem';
import { createTradeSystemFactories } from '../trade/tradeSystem';
import { createSprayBaySystem } from '../garage/sprayBaySystem';
import { createWorldSystem } from '../world/worldSystem';
import { createPedSystem } from '../peds/pedSystem';
import { createHeatSystem } from '../heat/heatSystem';
import { createDeskSergeantSystem } from '../heat/deskSergeant';
import { createPoliceSystem } from '../police/policeSystem';
import { createSignalSystem, createTrafficSystem } from '../traffic/trafficSystem';
import { createDebugOverlaySystem } from './debugOverlay';
import { createPhysicsSystem } from './physicsSystem';
import { createSandboxSystem } from './sandbox';
import { createOnlineSystemFactories } from '../net/onlineBoot';
import { createWeaponSystem } from '../weapons/weaponSystem';
import { createWeaponViewSystem } from '../weapons/weaponView';
import { createTapTargetSystem } from '../weapons/tapTargetSystem';
import { createBackRoomSystem } from '../weapons/backRoom/backRoomSystem';
import { createHideoutSystem } from '../hideout/hideoutSystem';
import { createProfileSystem } from '../profile/profileSystem';
import { createFeedbackSystem } from '../feedback/feedbackSystem';

// Offline side activities: the rivals' drivers and the activity system share the race in progress.
const [createRivalDriverSystem, createActivitySystem] = createActivitySystemFactories();
// Wilf's book (offline trade): the Vanes' runner at the wheel and the trade rules share the van on the road.
const [createTradeRunnerSystem, createTradeSystem] = createTradeSystemFactories();

// Online client: six systems loaded on demand (net/onlineBoot.ts; the three net ones share one connection).
const [createNetInSystem, createNetProxySystem, createNetSendSystem, createNetViewSystem, createOnlineUiSystem, createOnlineGameSystem] = createOnlineSystemFactories();

/**
 * SYSTEM REGISTRY. One line per system; execution order comes from each system's `order`
 * (see core/system.ts), NOT from position in this list. Add your line inside your team's block
 * to keep merges conflict-free.
 */
export const SYSTEMS: SystemFactory[] = [
  // ---- game-director (core, physics, player, vehicles, camera)
  createPlayerSystem, // 200
  createSandboxSystem, // 260 (scaffold test content)
  createVehicleSystem, // 300
  createPhysicsSystem, // 400
  createVehiclePostSystem, // 450
  createPropSystem, // 460
  createCameraRigSystem, // 600
  createVehicleViewSystem, // 710
  createPlayerViewSystem, // 711
  createOcclusionSilhouetteSystem, // 712 (the player / their car tinted through whatever hides them; docs/MOBILE.md)
  createVehicleFxSystem, // 720
  createParticleSystem, // 730
  createDebugOverlaySystem, // 795

  // ---- world-artist (world, render, fx)
  createWorldSystem, // 100
  createTimeOfDaySystem, // 140 (borough clock, Settings → Time of day, mission moods)
  createSceneLightingSystem, // 150
  createAmbientFxSystem, // 720
  createEnvFxSystem, // 725
  createExplosionFxSystem, // 726 (a car going up: shockwave ring, ground burst, debris chunks, peds knocked flat; docs/PERF.md)
  createJuiceSystem, // 735 (hit-stop, extra impact fx)
  createFootstepFxSystem, // 736 (footstep dust / puddle splashes)
  createImpactFxSystem, // 719 (the impact kit: per-surface bullet marks, sparks / dust and impact sounds; docs/WEAPONS.md)
  createMeleeFxSystem, // 737 (a blow landing: pop, motes, camera kick, hit-stop by weight, the knife's slash, scuffs on cars)
  createRenderSystem, // 790

  // ---- systems-ai (traffic, peds, police, heat)
  createSignalSystem, // 120
  createTrafficSystem, // 240 (civilian traffic, parked cars, vehicleGrid)
  createPedSystem, // 245
  createPoliceSystem, // 250 (police units, foot officers, roadblocks, busted/wasted)
  createHeatSystem, // 520 (wanted level, witnesses, sightings, search)

  // ---- missions-ux (missions, economy, save, ui, audio)
  createSaveSystem, // 505
  createEconomySystem, // 510
  createMissionSystem, // 530
  createDeskSergeantSystem, // 531 (a word with the desk sergeant: pay a wanted level off from a phone box, offline; docs/POLICE.md)
  createAudioSystem, // 650
  createUiSystem, // 750
  createTouchControlsSystem, // 758 (phones/tablets: on-screen stick + buttons, mobile shell; docs/MOBILE.md)
  createBookSystem, // 761 (the little black book: jobs, inbox + bleeper, contacts, gang/crew; docs/UI.md)
  createOnboardingSystem, // 762 (intro note, "what next" tracker + pointer, contextual tips; docs/UI.md)
  createRouteArrowsSystem, // 763 (route arrows painted on the road to the objective / map mark; docs/UI.md)
  createRivalDriverSystem, // 266 (street-race rivals at the wheel; docs/ACTIVITIES-OFFLINE.md)
  createActivitySystem, // 535 (races, minicab shifts, Halloran's workshop, matchbooks; docs/ACTIVITIES-OFFLINE.md)
  createMayhemSystem, // 536 (mayhem challenges: the half-bricks; docs/ACTIVITIES-OFFLINE.md)
  createSprayBaySystem, // 537 (Pike's spray bay: drive in, garage mode, paint / wheels / neon / stance; docs/ACTIVITIES-OFFLINE.md)
  createCarJobSystem, // 538 (cars with a job in them: get in, a voice offers a job; docs/ACTIVITIES-OFFLINE.md)
  createContactsSystem, // 539 (contact ladders: standing with Mags, Halloran, Sid, Lenny, Wilf, Pike, Dot, Rita; docs/CONTACTS.md)
  createTradeRunnerSystem, // 268 (the Vanes' runner van at the wheel; docs/TRADE-OFFLINE.md)
  createTradeSystem, // 540 (Wilf's book: the offline trade round, Lenny's tips, the Vanes' runner; docs/TRADE-OFFLINE.md)
  ...createLoadingSteps(), // 1..799: honest loading-progress steps

  // ---- weapons (offline arsenal, melee, firearms, the fence; docs/WEAPONS.md)
  createWeaponSystem, // 205 (after the player controller: aim stance, swings, shots, pick-ups)
  createWeaponViewSystem, // 713 (weapon in hand, muzzle flash, tracers, HUD, armed-police telegraph)
  createTapTargetSystem, // 714 (tap a person or a car to lock onto them; right-click on desktop)
  createBackRoomSystem, // 715 (Sid's hut in Gorringe's yard: walk in, the back room, the counter)
  createHideoutSystem, // 716 (online: the gang's hideout behind 9 Pilcher Street; docs/HIDEOUT.md)
  createProfileSystem, // 717 (the profile page: you and your motor in the lock-up yard, the record on a card; docs/UI.md)

  // ---- client-integration (online: src/net, src/online-ui) — loaded and started when the player goes online
  createNetInSystem!, // 190 (corrections, resume, car hand-back)
  createNetProxySystem!, // 395 (remote cars → interpolated server pose, before physics)
  createNetSendSystem!, // 455 (upload predicted pose at 20 Hz)
  createNetViewSystem!, // 712 (other players on foot)
  createOnlineUiSystem!, // 756 (sign-in, connection chip, reconnect overlay, name tags)
  createOnlineGameSystem!, // 757 (online gameplay UI: wallet/bank/cargo HUD, panels, places, gang, chat, coop)

  // ---- operations (src/feedback): "Report a problem", automatic telemetry, the "new version" card
  createFeedbackSystem, // 770
];
